import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import {
  Grid,
  Dialog,
  Tooltip,
  IconButton,
  DialogTitle,
  DialogContent,
  Button
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CloseIcon } from '@/common/icons/CloseIcon';
import closeOutline from '@iconify-icons/eva/close-outline';
import { makeStyles } from '@mui/styles';
import { get } from 'lodash';

import { MIconButton } from '../../../../../../../@material-extend';
import { ModalActions } from '@/RestoreComponent/ModalActions';
import axios from 'axios';
import CONFIG from '@/config';

const useStyles = makeStyles(theme => ({
  fileInput: {
    width: 6,
    height: 26,
    visibility: 'hidden'
  },
  sizeError: {
    color: theme.palette.error.dark
  },
  size: {
    color: 'black'
  },
  filename: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: 340,
    marginRight: 16,
    whiteSpace: 'nowrap',
    display: 'block'
  }
}));
const getSizeForDisplay = v => `${Math.floor(parseInt(v) / 1000)}KB`;

const UploadFileModal = ({ closeModal, customerId, uploadComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDisplay, setErrorDisplay] = useState('');
  const [size, setSize] = useState(0);
  const [name, setName] = useState('');
  const [open, setOpen] = useState(true);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const classes = useStyles();

  const upload = async () => {
    setIsSubmitting(true);

    const formData = new FormData();
    const fileInput = document.getElementById('upload-file');
    const file = get(fileInput, 'files[0]');
    formData.append('file_content', file, file.name);
    formData.append('filename', file.name);
    formData.append('filetype', file.type);
    formData.append('customer_id', customerId);

    try {
      await axios.post(
        CONFIG.API + '/pos_customer/upload_document/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization:
              'JWT ' + (localStorage.getItem('RES_AUTH_TOKEN') || '')
          }
        }
      );
      enqueueSnackbar(`File upload complete.`, {
        variant: 'success',
        action: (key: any) => (
          <MIconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon />
          </MIconButton>
        )
      });
      uploadComplete();
    } catch (e) {
      enqueueSnackbar('A problem was encountered uploading the document.', {
        variant: 'error'
      });
    }
    setIsSubmitting(false);
    closeModal(true);
  };

  const handleFileRead = e => {
    const fileReader = new FileReader();
    fileReader.onload = e => {
      if (fileReader.readyState === 2) {
        const size = e.total;
        const uploadLimit = CONFIG.FILE_UPLOAD_LIMIT || 500000;
        setSize(size);
        if (size > uploadLimit) {
          setErrorDisplay(
            `The file size is over the allowed limit of ${
              uploadLimit / 1000
            }KB.`
          );
        } else {
          setErrorDisplay('');
        }
      }
    };
    fileReader.readAsDataURL(e.target.files[0]);
    setName(get(e, 'currentTarget.files[0].name'));
  };
  const overSizeLimit = size > CONFIG.FILE_UPLOAD_LIMIT;
  const sizeClass = overSizeLimit ? classes.sizeError : classes.size;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          background: '#fafafa',
          padding: '10px 15px 10px 30px',
          justifyContent: 'space-between'
        }}
      >
        <h3>Upload File</h3>
        <Tooltip arrow title="Close">
          <IconButton onClick={closeModal}>
            <Icon icon={closeOutline} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Grid container pt={3} mt={1}>
          <Grid item xs={3}>
            <Button variant="contained" component="label">
              Choose File
              <input
                className={classes.fileInput}
                id="upload-file"
                name="file"
                type="file"
                accept=".pdf,.jpeg,.png,.git"
                onChange={handleFileRead}
              />
            </Button>
          </Grid>
          <Grid item xs={8} pt={1} className={classes.filename}>
            {name}
          </Grid>
          <Grid item xs={1} pt={1} className={sizeClass}>
            {getSizeForDisplay(size)}
          </Grid>
          <Grid item xs={12} pt={4}>
            <div className={classes.sizeError}>{errorDisplay}&nbsp;</div>
          </Grid>
        </Grid>
      </DialogContent>
      <ModalActions>
        <Button variant="outlined" onClick={closeModal}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          disabled={overSizeLimit || size === 0}
          variant="contained"
          loading={isSubmitting}
          onClick={upload}
        >
          Upload
        </LoadingButton>
      </ModalActions>
    </Dialog>
  );
};

export default UploadFileModal;
